import type React from 'react';
import { PageTitle } from '@/hooks/usePageTitle';
import {
  ClosingBand,
  PageHero,
  QuestionList,
  QuestionSection,
  RequestAccessButton,
  TextLink,
} from '@/components/marketing/Sections';
import { media } from '@/content/media';

/* ==========================================================================
   FOR VENUES. Personas 1, 2 and 4 in docs/plans/MARKETING_SITE.md: the
   person at a small venue who books the shows, the host without a room, and
   whoever runs the board. One show in order, from booking to settling up,
   each step a question the booker would ask. Every claim here maps to a
   shipped feature. Recurring nights are a Pro feature (#860); the pricing
   page lists them, so a claim here must say it is Pro.
   ========================================================================== */

const ForVenues: React.FC = () => (
  <>
    <PageTitle title="For venues" />
    <PageHero
      title="For whoever books the room"
      actions={
        <>
          <RequestAccessButton />
          <TextLink to="/pricing">See pricing →</TextLink>
        </>
      }
    >
      <p>
        The bartender with the calendar, the promoter renting the room for a night, the open mic
        host with a clipboard. This is how a show goes in Showliner, from the first text to
        settling up.
      </p>
    </PageHero>

    <QuestionList>
      <QuestionSection
        question="How do I book a night?"
        media={media.adminEventList}
        frame="browser"
        caption="Upcoming shows and the month at a glance"
      >
        <p>
          Put the date on the calendar and add the acts. If an act is already on Showliner
          you'll find them, and if they aren't, which is most of them, you just type the name.
          Drag the bill into the order you want.
        </p>
        <p>
          You can pencil someone in while you're still talking to them. Nobody else on the bill
          sees a pencil until you tell that act about the show, so a support act never finds out
          you're courting a headliner. When you're ready, send each act a link by text, by email,
          or as a link you paste in the group chat.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What if there's money involved?"
      >
        <p>
          Send an offer with the terms on it: a flat guarantee, a split of the door, or a
          guarantee against or on top of a split. The act reads it on their link and accepts,
          counters, or passes. Accepting money asks them for a code we email them, so nobody
          agrees to terms by tapping the wrong thing.
        </p>
        <p>
          If you agreed on the phone, record the terms yourself. The act sees what you recorded
          and can send back their own if you got it wrong. Showcases and tips-only nights work
          too, and a confirmed act doesn't need terms at all.
        </p>
      </QuestionSection>

      <QuestionSection
        question="How do I stop chasing load-in times?"
        media={media.adminEventDetail}
        frame="browser"
        caption="The bill and the schedule, with two party sizes still outstanding"
        flip
      >
        <p>
          Post what you already know once: load-in, doors, set times, parking. Then ask every act
          for what you need from them, like an input list or how many people they're bringing.
          They answer on the same link, and your show page shows what's still outstanding and
          which act it's waiting on. An act you add a week later gets asked too, without you
          remembering to.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What does the sound engineer get?"
        media={media.adminStagePlot}
        frame="browser"
        caption="The house backline, in orange, under an act's plot"
        flip
      >
        <p>
          Draw the house backline once as a stage plot. Each act draws theirs on top of it, so
          the engineer gets one plot per act with the house gear already in place, plus an input
          list, on a tablet at the board or as a PDF for anyone who'd rather have paper.
        </p>
        <p>
          Acts can save their plot as a template (full band, acoustic, solo) and bring it to every
          room they play, which is how the plot stops living on the bassist's phone.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What's on the TV?"
        media={media.displayBeforeShow}
        frame="tv"
        caption="Before the first act: the bill, the QR code, and the chat"
      >
        <p>
          Start the show and open the room's display in any web browser that's connected to the
          TV, like a smart TV's browser or a laptop on an HDMI cable. There's no box to buy. It
          shows the act on stage, who's next, the chat, and the QR code, and between sets it can
          play your own slides and loops (that part is on Pro).
        </p>
        <p>
          You run the room from a laptop or your phone. Lock everyone's phones for the quiet
          song so the chat stops and the room watches the stage, put a question up as a poll,
          or put a banner with a link on every phone in the room. Chat runs through a filter, and
          you can add your own words to it.
        </p>
      </QuestionSection>

      <QuestionSection
        question="What do fans do once they're in?"
        media={media.phoneRoom}
        frame="phone"
        caption="A fan's phone, in the room"
        flip
      >
        <p>
          They chat, and it shows up on the TV. They tip the act with real money, and the act
          keeps the whole tip. If the act takes requests, fans request songs off the act's list
          and back them with money or with the act's own currency.
        </p>
        <p>
          Time in the room earns them your house currency, called Housepower, which they spend on
          effects that play on the screen. The balance is still there the next time they come to
          your room, so your regulars have something that builds up.
        </p>
      </QuestionSection>

      <QuestionSection question="What about an open mic?">
        <p>
          Choose Start now, then Open mic, and the room is live with nobody on the bill yet.
          Walk-ups scan the code and sign up from their phones, you approve them into the running
          order from the console, and anyone who gave us their number gets a text when they're on
          deck.
        </p>
      </QuestionSection>

      <QuestionSection question="How do we settle up?">
        <p>
          After the show, enter what the door did. Showliner works out what each act is owed from
          the terms you agreed, shows the math line by line, and sends it to the act to sign off
          on their phone. Then you pay them however you already do, cash included, and mark it
          paid. Showliner keeps the record and never touches the money.
        </p>
      </QuestionSection>
    </QuestionList>

    <ClosingBand
      title="Free for a room that books a few shows a month"
      actions={
        <>
          <RequestAccessButton />
          <TextLink to="/pricing">See plans →</TextLink>
        </>
      }
    >
      <p>
        Venue Pro is $12 a month, or $120 a year, for rooms running shows every week. We're
        adding venues a few at a time while it's early, so it starts with a request.
      </p>
    </ClosingBand>
  </>
);

export default ForVenues;
